import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  addTalentDiagnosticsQuestionOption,
  createTalentDiagnosticsBenchmark,
  createTalentDiagnosticsQuestion,
  createTalentDiagnosticsRecommendation,
  createTalentDiagnosticsSection,
  deleteTalentDiagnosticsBenchmark,
  deleteTalentDiagnosticsQuestion,
  deleteTalentDiagnosticsRecommendation,
  deleteTalentDiagnosticsSection,
  getTalentDiagnosticsBenchmarks,
  getTalentDiagnosticsDefinitions,
  getTalentDiagnosticsQuestions,
  getTalentDiagnosticsRecommendations,
  getTalentDiagnosticsSections,
  reorderTalentDiagnosticsQuestions,
  reorderTalentDiagnosticsSections,
  updateTalentDiagnosticsBenchmark,
  updateTalentDiagnosticsQuestion,
  updateTalentDiagnosticsRecommendation,
  updateTalentDiagnosticsSection,
  TalentDiagnosticsBenchmark,
  TalentDiagnosticsDefinition,
  TalentDiagnosticsQuestion,
  TalentDiagnosticsRecommendation,
  TalentDiagnosticsSection,
} from '../api/talentDiagnosticsApi';

const QUERY_OPTIONS = {
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  refetchOnMount: false,
  retry: false,
  staleTime: 5 * 60 * 1000,
} as const;

const QUERY_KEYS = {
  definitions: ['talent-diagnostics-definitions'],
  sections: (definitionId: string) => ['talent-diagnostics-sections', definitionId],
  questions: (sectionId: string) => ['talent-diagnostics-questions', sectionId],
  benchmarks: (definitionId: string) => ['talent-diagnostics-benchmarks', definitionId],
  recommendations: (definitionId: string) => ['talent-diagnostics-recommendations', definitionId],
};

export const useTalentDiagnosticsDefinitions = () =>
  useQuery<TalentDiagnosticsDefinition[]>({
    queryKey: QUERY_KEYS.definitions,
    queryFn: getTalentDiagnosticsDefinitions,
    ...QUERY_OPTIONS,
  });

export const useTalentDiagnosticsSections = (definitionId: string) =>
  useQuery<TalentDiagnosticsSection[]>({
    queryKey: QUERY_KEYS.sections(definitionId),
    queryFn: () => getTalentDiagnosticsSections(definitionId),
    enabled: Boolean(definitionId),
    ...QUERY_OPTIONS,
  });

export const useTalentDiagnosticsQuestions = (sectionId: string) =>
  useQuery<TalentDiagnosticsQuestion[]>({
    queryKey: QUERY_KEYS.questions(sectionId),
    queryFn: () => getTalentDiagnosticsQuestions(sectionId),
    enabled: Boolean(sectionId),
    ...QUERY_OPTIONS,
  });

export const useTalentDiagnosticsBenchmarks = (definitionId: string) =>
  useQuery<TalentDiagnosticsBenchmark[]>({
    queryKey: QUERY_KEYS.benchmarks(definitionId),
    queryFn: () => getTalentDiagnosticsBenchmarks(definitionId),
    enabled: Boolean(definitionId),
    ...QUERY_OPTIONS,
  });

export const useTalentDiagnosticsRecommendations = (definitionId: string) =>
  useQuery<TalentDiagnosticsRecommendation[]>({
    queryKey: QUERY_KEYS.recommendations(definitionId),
    queryFn: () => getTalentDiagnosticsRecommendations(definitionId),
    enabled: Boolean(definitionId),
    ...QUERY_OPTIONS,
  });

export const useCreateTalentDiagnosticsSection = (definitionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTalentDiagnosticsSection,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.sections(definitionId) });
      toast.success('Section created successfully');
    },
    onError: () => toast.error('Failed to create section'),
  });
};

export const useUpdateTalentDiagnosticsSection = (definitionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<TalentDiagnosticsSection> }) => updateTalentDiagnosticsSection(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.sections(definitionId) });
      toast.success('Section updated successfully');
    },
    onError: () => toast.error('Failed to update section'),
  });
};

export const useDeleteTalentDiagnosticsSection = (definitionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteTalentDiagnosticsSection,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.sections(definitionId) });
      toast.success('Section deleted successfully');
    },
    onError: () => toast.error('Failed to delete section'),
  });
};

export const useReorderTalentDiagnosticsSections = (definitionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: reorderTalentDiagnosticsSections,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.sections(definitionId) });
      toast.success('Sections reordered successfully');
    },
    onError: () => toast.error('Failed to reorder sections'),
  });
};

export const useCreateTalentDiagnosticsQuestion = (sectionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTalentDiagnosticsQuestion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.questions(sectionId) });
      toast.success('Question created successfully');
    },
    onError: () => toast.error('Failed to create question'),
  });
};

export const useUpdateTalentDiagnosticsQuestion = (sectionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<TalentDiagnosticsQuestion> }) => updateTalentDiagnosticsQuestion(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.questions(sectionId) });
      toast.success('Question updated successfully');
    },
    onError: () => toast.error('Failed to update question'),
  });
};

export const useDeleteTalentDiagnosticsQuestion = (sectionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteTalentDiagnosticsQuestion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.questions(sectionId) });
      toast.success('Question deleted successfully');
    },
    onError: () => toast.error('Failed to delete question'),
  });
};

export const useReorderTalentDiagnosticsQuestions = (sectionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: reorderTalentDiagnosticsQuestions,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.questions(sectionId) });
      toast.success('Questions reordered successfully');
    },
    onError: () => toast.error('Failed to reorder questions'),
  });
};

export const useAddTalentDiagnosticsQuestionOption = () =>
  useMutation({
    mutationFn: ({ questionId, payload }: { questionId: string; payload: Array<{ key: string; text: string; score?: number }> }) => addTalentDiagnosticsQuestionOption(questionId, payload),
    onSuccess: () => toast.success('Option added successfully'),
    onError: () => toast.error('Failed to add option'),
  });

export const useCreateTalentDiagnosticsBenchmark = (definitionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTalentDiagnosticsBenchmark,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.benchmarks(definitionId) });
      toast.success('Benchmark created successfully');
    },
    onError: () => toast.error('Failed to create benchmark'),
  });
};

export const useUpdateTalentDiagnosticsBenchmark = (definitionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<TalentDiagnosticsBenchmark> }) => updateTalentDiagnosticsBenchmark(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.benchmarks(definitionId) });
      toast.success('Benchmark updated successfully');
    },
    onError: () => toast.error('Failed to update benchmark'),
  });
};

export const useDeleteTalentDiagnosticsBenchmark = (definitionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteTalentDiagnosticsBenchmark,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.benchmarks(definitionId) });
      toast.success('Benchmark deleted successfully');
    },
    onError: () => toast.error('Failed to delete benchmark'),
  });
};

export const useCreateTalentDiagnosticsRecommendation = (definitionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTalentDiagnosticsRecommendation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.recommendations(definitionId) });
      toast.success('Recommendation created successfully');
    },
    onError: () => toast.error('Failed to create recommendation'),
  });
};

export const useUpdateTalentDiagnosticsRecommendation = (definitionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<TalentDiagnosticsRecommendation> }) => updateTalentDiagnosticsRecommendation(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.recommendations(definitionId) });
      toast.success('Recommendation updated successfully');
    },
    onError: () => toast.error('Failed to update recommendation'),
  });
};

export const useDeleteTalentDiagnosticsRecommendation = (definitionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteTalentDiagnosticsRecommendation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.recommendations(definitionId) });
      toast.success('Recommendation deleted successfully');
    },
    onError: () => toast.error('Failed to delete recommendation'),
  });
};
